// src/app/deepLinkResolve.ts
//
// EMİR #94.4 — DERİN BAĞLANTI ÇÖZÜMLEME (tek paylaşılan yardımcı)
//
// ── NEDEN TEK YARDIMCI, BEŞ YAMA DEĞİL ────────────────────────────────────
// `appRouting.ts` içinde BEŞ dal aynı hatayı ayrı ayrı yapıyordu (dört
// fonksiyon): `!exists()` ve `catch` dallarının hepsi `replaceState("/")`.
// Beşini tek tek yamak, gelecek ay eklenen 6. derin bağlantının aynı hatayı
// yeniden doğurmasına izin verirdi. Karar aynı gerekçeyle #95.1'deki kayan-
// pencere primitifinde de verilmişti: tekrar eden hata, tekrar eden yamayla
// değil TEK PRİMİTİFLE kapatılır.
//
// ── ÜÇ SONUÇ, AMA İKİSİ KASITLI OLARAK AYNI GÖRÜNÜR ───────────────────────
// Okuma başarısız olduğunda üç ayrı gerçeklik vardır:
//   BULUNDU      → içerik var ve okundu
//   YOK          → içerik gerçekten yok (silinmiş / hiç olmamış)
//   ULASILAMADI  → içerik hakkında BİLGİMİZ YOK (ağ/sunucu erişilemedi)
//
// 🔒 MAHREMİYET KARARI (mimar şartı — otomatik "üçüncü mesaj" TUZAK):
// Dördüncü bir gerçeklik daha var: İZİN YOK (gizli hesap, engellenmiş
// kullanıcı, rules-denied). Bunu ayrı bir mesaja çevirmek CAZİP ama YANLIŞTIR:
// "bu içerik var ama göremezsin" demek, gizli hesabın/engelin VARLIĞINI
// sızdırır. Instagram bunu bilerek "bulunamadı"ya yıkar.
// Bu yüzden:
//   · `permission-denied` → YOK ile AYNI kutuya düşer (kasıtlı, kusur DEĞİL)
//   · yalnız AĞ durumları ULASILAMADI olur (içerik hakkında bilgi sızdırmaz)
// Sanal dünya invariant'ı da bu yönde yazılır: "ULASILAMADI ayırt edilebilir
// olmalı" + "YOK ile İZİN YOK ayırt edilebilir OLMAMALI". İkisinin AYRIŞMASI
// bir mahremiyet hatasıdır; birleşmesi tasarımdır.
//
// ── URL KORUNUR ───────────────────────────────────────────────────────────
// Eski davranış adres çubuğunu siliyordu (`replaceState("/")`) → kullanıcı
// yenilese bile derin bağlantı geri gelmiyordu, tekrar deneme imkânı yok
// oluyordu. `/map` URL'ini koruyor; doğrusu bu. Çözümleyici hiçbir dalda URL'e
// DOKUNMAZ — karar çağrı yerinin, ama varsayılan "koru"dur.

/** Derin bağlantı çözümleme sonucu. */
export type DerinDurum = "BULUNDU" | "YOK" | "ULASILAMADI";

export interface DerinSonuc<T> {
  durum: DerinDurum;
  veri?: T;
  /** Tanı için; kullanıcıya GÖSTERİLMEZ (mahremiyet + gürültü). */
  hataKodu?: string;
}

/**
 * AĞ DURUMU MU, İÇERİK DURUMU MU?
 *
 * Firestore hata kodları: `unavailable` (bağlanamadı), `deadline-exceeded`
 * (zaman aşımı), `internal`/`aborted` (geçici sunucu), `cancelled`.
 * Bunlar içerik hakkında HİÇBİR ŞEY söylemez → ULASILAMADI.
 *
 * `permission-denied` ve `not-found` ise içerik/izin durumudur → YOK
 * (mahremiyet gereği ayırt edilmez).
 *
 * ⚠️ Bilinmeyen kod → ULASILAMADI (güvenli taraf): emin değilsek kullanıcıya
 * "yok" demeyiz. Yanlış "yok", yanlış "ulaşılamadı"dan daha zararlıdır —
 * ilki var olan içeriği yok ilan eder, ikincisi yalnız tekrar denemeyi önerir.
 */
const AG_KODLARI = new Set([
  "unavailable",
  "deadline-exceeded",
  "internal",
  "aborted",
  "cancelled",
  "resource-exhausted",
  "unauthenticated",
]);
const ICERIK_KODLARI = new Set(["permission-denied", "not-found", "invalid-argument"]);

export function agHatasiMi(hata: any): boolean {
  const kod = String(hata?.code || "").replace(/^firestore\//, "");
  if (ICERIK_KODLARI.has(kod)) return false;
  if (AG_KODLARI.has(kod)) return true;
  // Kodsuz hatalar (TypeError: Failed to fetch, AbortError…) ağ sayılır.
  return true;
}

/**
 * OKUMA SONUCU — çağrı yeri `sunucudan`u BİLDİRMEK ZORUNDA.
 *
 * ⚠️ Bu sözleşme bilerek zahmetli: ilk yazımda okuyucu `T | null` döndürüyordu
 * ve `getDoc` için `snap.exists() ? snap : null` diye sarmalanıyordu. O hâliyle
 * primitif SADECE HATA KODLARINA bakıyordu — ve `authStore` hatasının şekli tam
 * olarak "hata YOK, kod YOK" idi:
 *     snapshot BAŞARILI · exists()===false · metadata.fromCache===true
 * Yani düzeltmeye çalıştığımız hatayı primitife gömmüş olurduk. Artık yokluk,
 * ancak SUNUCU söylediyse yokluktur.
 */
export interface Okuma<T> {
  varMi: boolean;
  /** true = sunucu cevabı · false = önbellek/doğrulanmamış */
  sunucudan: boolean;
  veri?: T;
}

/** Firestore doküman snapshot'ını Okuma'ya çevirir (metadata KAYBOLMAZ). */
export function docOkumasi<T = any>(snap: any): Okuma<T> {
  return {
    varMi: !!snap?.exists?.(),
    sunucudan: snap?.metadata?.fromCache === false,
    veri: snap,
  };
}

/** Firestore sorgu snapshot'ını Okuma'ya çevirir. */
export function sorguOkumasi<T = any>(snap: any, liste: T): Okuma<T> {
  const bos = !liste || (Array.isArray(liste) && liste.length === 0);
  return {
    varMi: !bos,
    sunucudan: snap?.metadata?.fromCache === false,
    veri: liste,
  };
}

/**
 * Bir okuma işlemini üç duruma çevirir.
 *
 * KARAR SIRASI (sıra ÖNEMLİ):
 *   1. varMi                      → BULUNDU
 *   2. !varMi && sunucudan        → YOK          (sunucu söyledi, güvenilir)
 *   3. !varMi && !sunucudan       → ULASILAMADI  (kanıtın yokluğu ≠ yokluğun kanıtı)
 *   4. istisna: İZİN kodları önce → YOK          (mahremiyet — aşağıdaki nota bak)
 *   5. istisna: kalan her şey     → ULASILAMADI  (güvenli taraf)
 *
 * 🔒 ÖNCELİK NOTU: güvenli-taraf kuralı MAHREMİYET kuralını EZMEZ. İzin kaynaklı
 * hiçbir durum ULASILAMADI'ya düşmez — düşerse "bu içerik var ama göremezsin"
 * anlamı çıkar ve gizli hesabın/engelin varlığı sızar. Bu yüzden izin dalları
 * ÖNCE eşlenir; güvenli-taraf yalnız geriye kalanlara uygulanır.
 */
export async function derinBaglantiyiCoz<T>(oku: () => Promise<Okuma<T>>): Promise<DerinSonuc<T>> {
  try {
    const o = await oku();
    if (o.varMi) return { durum: "BULUNDU", veri: o.veri };
    return o.sunucudan
      ? { durum: "YOK" }
      : { durum: "ULASILAMADI", hataKodu: "bos-snapshot-onbellekten" };
  } catch (hata: any) {
    const kod = String(hata?.code || hata?.name || "bilinmiyor");
    // İZİN önce (mahremiyet), güvenli-taraf sonra.
    return agHatasiMi(hata)
      ? { durum: "ULASILAMADI", hataKodu: kod }
      : { durum: "YOK", hataKodu: kod };
  }
}
